import type { Middleware } from '@reduxjs/toolkit';
import { clearEquipmentRedo } from '../equipment/equipmentSlice';
import { clearFuture } from './historySlice';

/** Minimum shape the middleware reads — keeps it usable from focused tests. */
type HistoryAwareState = {
  history: { past: unknown[]; future: unknown[] };
  equipment: { history: { past: unknown[]; future: unknown[] } };
};

/**
 * Actions that move entries between the past / future stacks rather than
 * recording a new edit. They must not invalidate the other layer's redo stack —
 * a global redo legitimately grows one `past` while the other `future` waits.
 */
const HISTORY_CONTROL_ACTIONS = new Set([
  'history/performUndo',
  'history/performRedo',
  'history/clearFuture',
  'history/clearHistory',
  'history/setHistoryState',
  'equipment/undoEquipment',
  'equipment/redoEquipment',
  'equipment/clearEquipmentRedo',
  'equipment/loadEquipment',
]);

/**
 * Layout and equipment keep separate undo stacks, but the user sees one timeline
 * (see ./unifiedHistory). Recording a new edit in either layer has to discard
 * pending redos in *both*, otherwise a global redo could replay an edit that no
 * longer follows from the current state.
 */
export const redoInvalidationMiddleware: Middleware = (api) => (next) => (action) => {
  const type = (action as { type?: string }).type || '';
  if (HISTORY_CONTROL_ACTIONS.has(type)) return next(action);

  const before = api.getState() as HistoryAwareState;
  // Compare the top entries by identity: at the history cap a push is paired with
  // a shift, so stack length alone would miss the newly recorded edit.
  const layoutTop = before.history.past.at(-1);
  const equipmentTop = before.equipment.history.past.at(-1);

  const result = next(action);

  const after = api.getState() as HistoryAwareState;
  if (after.history.past.at(-1) !== layoutTop && after.equipment.history.future.length > 0) {
    api.dispatch(clearEquipmentRedo());
  }
  if (after.equipment.history.past.at(-1) !== equipmentTop && after.history.future.length > 0) {
    api.dispatch(clearFuture());
  }

  return result;
};
